import { Routes, Route, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion, AnimatePresence } from 'framer-motion'
import { usePostHogTracker } from './hooks/usePostHogTracker'

// Pages
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AuthCallbackPage from './pages/AuthCallbackPage'
import AdminDashboard from './pages/AdminDashboard'
import StudentDashboard from './pages/StudentDashboard'
import CourseListPage from './pages/CourseListPage'
import CourseDetailPage from './pages/CourseDetailPage'
import CreateCoursePage from './pages/CreateCoursePage'
import UserManagementPage from './pages/UserManagementPage'
import StudentListPage from './pages/StudentListPage'
import AdminStudentProfilePage from './pages/AdminStudentProfilePage'
import ProfilePage from './pages/ProfilePage'
import CertificatesPage from './pages/CertificatesPage'
// Student Realtime Projects pages
import StudentRealtimeProjectsPage from './pages/StudentRealtimeProjectsPage'
import ProjectViewer from './components/projects/ProjectViewer'
import ProjectDiagnostics from './pages/ProjectDiagnostics'
// import HackathonPage from './pages/HackathonPage' // HIDDEN: Hackathons/Internships temporarily hidden
// Phase pages - COMMENTED OUT (Student access removed, but kept for future use)
// import BRDPhasePage from './pages/BRDPhasePage'
// import UIUXPhasePage from './pages/UIUXPhasePage'
// import ArchitecturalDesignPhasePage from './pages/ArchitecturalDesignPhasePage'
// import CodeDevelopmentPhasePage from './pages/CodeDevelopmentPhasePage'
// import TestingPhasePage from './pages/TestingPhasePage';
// import TestingPhasePage';
import MyProjectSubmissionsPage from './pages/MyProjectSubmissionsPage';
// import DeploymentPhasePage from './pages/DeploymentPhasePage'
// Admin Projects pages - COMMENTED OUT (Admin side removed, but kept for students)
// import AdminProjectsPage from './pages/AdminProjectsPage'
// import StudentInternshipsPage from './pages/StudentInternshipsPage' // HIDDEN
import AdminProjectManagementPage from './pages/AdminProjectManagementPage'
import ProjectManagementDetailPage from './pages/ProjectManagementDetailPage'
// import AdminHackathonsPage from './pages/AdminHackathonsPage'; // HIDDEN
import AdminProjectSubmissionsPage from './pages/AdminProjectSubmissionsPage';
// import CreateHackathonPage from './pages/CreateHackathonPage' // HIDDEN
// import CreateGroupPage from './pages/CreateGroupPage' // HIDDEN
// import AdminChatPage from './pages/AdminChatPage'
import RealtimeProjectsLandingPage from './pages/RealtimeProjectsLandingPage'
// import HackathonLandingPage from './pages/HackathonLandingPage' // HIDDEN
// import PricingPage from './pages/PricingPage' // HIDDEN: Pricing temporarily hidden
import ContactPage from './pages/ContactPage'
import NotFoundPage from './pages/NotFoundPage'
// import InternshipsLandingPage from './pages/InternshipsLandingPage' // HIDDEN
// import AdminInternshipsPage from './pages/AdminInternshipsPage' // HIDDEN
// import MyInternshipSubmissionsPage from './pages/MyInternshipSubmissionsPage' // HIDDEN
import NotificationsPage from './pages/NotificationsPage'
import { FdeProgressProvider } from './fde/context/FdeProgressContext'
import FdeLayout from './fde/components/FdeLayout'
import FdeRoadmapPage from './fde/pages/FdeRoadmapPage'
import FdeItemPage from './fde/pages/FdeItemPage'
import FdeMyLearningPage from './fde/pages/FdeMyLearningPage'
import FdeCapstonePage from './fde/pages/FdeCapstonePage'

// Components
import ProtectedRoute from './components/auth/ProtectedRoute'
import LoadingSpinner from './components/common/LoadingSpinner'
import ErrorBoundary from './components/common/ErrorBoundary'
// import ChatNotification from './components/chat/ChatNotification'

// Hooks
import { useAuth } from './context/AuthContext'
import { ProjectProgressProvider } from './context/ProjectProgressContext'
import { NotificationProvider } from './context/NotificationContext'

function App() {
  console.log('App component is rendering')

  // Initialize PostHog tracker for page view tracking
  usePostHogTracker()

  try {
    const { user, loading } = useAuth()
    console.log('Auth state:', { user, loading })

    if (loading) {
      console.log('App is in loading state')
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      )
    }

    console.log('App is rendering main content')

    return (
      <ErrorBoundary>
        <ProjectProgressProvider>
          <Helmet>
            <title>GNANAM AI - AI-Powered Learning Platform</title>
            <meta name="description" content="Experience the future of education with GNANAM AI - your intelligent learning companion" />
          </Helmet>

          {/* <ChatNotification /> */}

          <NotificationProvider>
            <AnimatePresence mode="wait">
              <Routes>
                {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              {/* HIDDEN: Pricing route temporarily hidden */}
              {/* <Route path="/pricing" element={<PricingPage />} /> */}
              <Route path="/contact" element={<ContactPage />} />

              {/* Protected Routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/student/*"
                element={
                  <ProtectedRoute requiredRole="student">
                    <StudentDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  user ? (
                    <Navigate to={user.role === 'admin' ? '/admin' : '/student'} replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Course Routes */}
              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/courses" element={<CourseListPage />} />
              <Route path="/realtime-projects" element={<RealtimeProjectsLandingPage />} />
              <Route path="/realtime-projects/diagnostics" element={<ProjectDiagnostics />} />

              <Route
                path="/roadmap/fde"
                element={
                  <FdeProgressProvider>
                    <FdeLayout />
                  </FdeProgressProvider>
                }
              >
                <Route index element={<FdeRoadmapPage />} />
                <Route path="my-learning" element={<FdeMyLearningPage />} />
                <Route path="capstone" element={<FdeCapstonePage />} />
                <Route path="item/:stageSlug/:itemSlug" element={<FdeItemPage />} />
              </Route>
              <Route path="/fde-capstone" element={<Navigate to="/roadmap/fde/capstone" replace />} />
              <Route path="/capstone/fde" element={<Navigate to="/roadmap/fde/capstone" replace />} />
              <Route path="/fde/capstone" element={<Navigate to="/roadmap/fde/capstone" replace />} />
              {/* HIDDEN: Hackathons & Internships routes temporarily hidden */}
              {/* <Route path="/hackathons" element={<HackathonLandingPage />} /> */}
              {/* <Route path="/internships" element={<InternshipsLandingPage />} /> */}
              {/* <Route path="/admin/hackathons" element={<AdminHackathonsPage />} /> */}
              <Route path="/admin/project-submissions" element={<AdminProjectSubmissionsPage />} />
              {/* <Route path="/admin/hackathons/create" element={<CreateHackathonPage />} /> */}
              <Route path="/courses/:id" element={<CourseDetailPage />} />

              {/* Realtime Projects Routes - Student access */}
              <Route
                path="/student/realtime-projects"
                element={
                  <ProtectedRoute>
                    <StudentRealtimeProjectsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/student/realtime-projects/:projectId"
                element={
                  <ProtectedRoute>
                    <ProjectViewer />
                  </ProtectedRoute>
                }
              />

              {/* HIDDEN: Student Hackathons & Internships Routes temporarily hidden */}
              {/* <Route path="/student/hackathons" element={<ProtectedRoute><HackathonPage /></ProtectedRoute>} /> */}
              {/* <Route path="/student/internships" element={<ProtectedRoute><StudentInternshipsPage /></ProtectedRoute>} /> */}
              {/* <Route path="/student/internship-submissions" element={<ProtectedRoute><MyInternshipSubmissionsPage /></ProtectedRoute>} /> */}

              {/* Realtime Projects Phase Routes - COMMENTED OUT (Student access removed, but pages kept for future use) */}
              {/* <Route
              path="/student/realtime-projects/:projectId/brd"
              element={
                <ProtectedRoute>
                  <BRDPhasePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/realtime-projects/:projectId/uiux"
              element={
                <ProtectedRoute>
                  <UIUXPhasePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/realtime-projects/:projectId/architectural"
              element={
                <ProtectedRoute>
                  <ArchitecturalDesignPhasePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/realtime-projects/:projectId/code-development"
              element={
                <ProtectedRoute>
                  <CodeDevelopmentPhasePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/realtime-projects/:projectId/testing"
              element={
                <ProtectedRoute>
                  <TestingPhasePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/my-project-submissions"
              element={
                <ProtectedRoute>
                  <MyProjectSubmissionsPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/realtime-projects/:projectId/deployment"
              element={
                <ProtectedRoute>
                  <DeploymentPhasePage />
                </ProtectedRoute>
              }
            /> */}

              {/* Admin Routes */}
              <Route
                path="/admin/courses/create"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <CreateCoursePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/students"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <StudentListPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/students/:id"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminStudentProfilePage />
                  </ProtectedRoute>
                }
              />

              {/* Admin Projects Routes - COMMENTED OUT (Admin side removed, but kept for students) */}
              {/* <Route
              path="/admin/projects"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminProjectManagementPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/project-management"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminProjectManagementPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/projects/:projectId"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ProjectManagementDetailPage />
                </ProtectedRoute>
              }
            /> */}

              {/* HIDDEN: Admin Internships & Hackathon Routes temporarily hidden */}
              {/* <Route path="/admin/internships" element={<ProtectedRoute requiredRole="admin"><AdminInternshipsPage /></ProtectedRoute>} /> */}
              {/* <Route path="/admin/hackathons" element={<ProtectedRoute requiredRole="admin"><AdminHackathonsPage /></ProtectedRoute>} /> */}
              {/* <Route path="/admin/hackathons/create" element={<ProtectedRoute requiredRole="admin"><CreateHackathonPage /></ProtectedRoute>} /> */}
              {/* <Route path="/admin/hackathons/create-group" element={<ProtectedRoute requiredRole="admin"><CreateGroupPage /></ProtectedRoute>} /> */}

              {/* Chat Management Route - COMMENTED OUT */}
              {/* <Route
              path="/admin/chat"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminChatPage />
                </ProtectedRoute>
              }
            /> */}

              <Route
                path="/admin/project-management/:projectId"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <ProjectManagementDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Profile Route */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />

              {/* Certificates Route */}
              <Route
                path="/certificates"
                element={
                  <ProtectedRoute>
                    <CertificatesPage />
                  </ProtectedRoute>
                }
              />

              {/* 404 Route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AnimatePresence>
          </NotificationProvider>
        </ProjectProgressProvider>
      </ErrorBoundary>
    )
  } catch (error) {
    console.error('Error in App component:', error)
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#fef2f2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '2rem', color: '#dc2626', marginBottom: '1rem' }}>
            Error in App Component
          </h1>
          <p style={{ color: '#991b1b' }}>
            {error.message}
          </p>
        </div>
      </div>
    )
  }
}

export default App